import { Component, DestroyRef, OnInit, PLATFORM_ID, computed, inject, input, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NotificacionesCampanaService } from 'src/app/services/notificaciones-campana/notificaciones-campana.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonicModule, PopoverController } from '@ionic/angular';

import { User } from 'src/app/interfaces/user';
import { SessionService } from 'src/app/services/session/session.service';
import { AlertasService } from 'src/app/services/alertas/alertas.service';
import { EventService } from 'src/app/services/event/event.service';
import { QuickComponentsService } from 'src/app/services/quick-components/quick-components.service';
import { SharedModule } from '../shared';
import { IconComponent } from '../icon/icon.component';

export type SeccionDeBarra = 'inicio' | 'categorias' | 'alertas' | 'perfil';

/** Qué pestaña marca la URL actual; Alertas y Perfil solo si el perfil es el propio. */
export function seccionActiva(url: string | null | undefined, userId: number | string | null | undefined): SeccionDeBarra | null {
  if (!url) return null;

  const ruta = url.split(/[?#]/)[0];

  if (ruta === '/' || ruta === '') return 'inicio';
  if (ruta.startsWith('/categorias/') || ruta === '/categorias') return 'categorias';
  if (ruta === '/cuenta/' || ruta === '/cuenta') return 'perfil';

  if (userId !== undefined && userId !== null && userId !== '') {
    const propio = `/user/${userId}/`;
    if (ruta.startsWith(`${propio}alerts/`)) return 'alertas';
    if (ruta.startsWith(propio)) return 'perfil';
  }

  return null;
}

/**
 * Barra inferior móvil: Inicio · Categorías · Comparte (botón redondo flotante) · Alertas ·
 * Perfil. Solo se ve bajo 993 px; en escritorio manda la cabecera. Sin sesión, Comparte,
 * Alertas y Perfil abren el acceso. Spec: odd/tasks/rediseno-chats-y-guardados.md, T9, S12.
 *
 * Los iconos son `app-icon` (sprite incrustado en el HTML), no `ion-icon`: así la barra sale
 * entera en el primer pintado, sin esperar a que arranque Ionic (31/10/2026).
 */
@Component({
    selector: 'app-barra-inferior',
    templateUrl: './barra-inferior.component.html',
    styleUrls: ['./barra-inferior.component.scss'],
    standalone: true,
    imports: [IonicModule, SharedModule, IconComponent],
})
export class BarraInferiorComponent implements OnInit {
  private session = inject(SessionService);
  private event = inject(EventService);
  private quick = inject(QuickComponentsService);
  private popover = inject(PopoverController);
  private destroyRef = inject(DestroyRef);
  private campana = inject(NotificacionesCampanaService);
  private alertas = inject(AlertasService);
  private esNavegador = isPlatformBrowser(inject(PLATFORM_ID));

  /** La URL actual, que publica `AppComponent`. */
  readonly url = input<string | undefined>(undefined);

  readonly usuario = signal<User | null>(null);

  readonly seccion = computed(() => seccionActiva(this.url(), this.usuario()?.id));

  readonly urlAlertas = computed(() => this.usuario() ? `/user/${this.usuario().id}/alerts/` : null);
  /**
   * Número de la pestaña Alertas: avisos de la campana sin leer más alertas recibidas sin
   * leer, lo mismo que enseña la pantalla Alertas en sus sub-pestañas «Avisos» y «Recibidas»
   * (odd/tasks/avisos-movil.md, S3). Tope «9+»; nada sin sesión.
   */
  readonly avisosSinLeer = computed(() => {
    const n = this.usuario() ? this.campana.unread() + this.alertas.recibidasSinLeer() : 0;
    return n > 0 ? (n > 9 ? '9+' : String(n)) : null;
  });
  // Perfil abre la pantalla Cuenta (odd/tasks/movil-cabecera-tarjeta-cuenta.md, S6); desde ahí, «Ver perfil público».
  readonly urlPerfil = computed(() => this.usuario() ? '/cuenta/' : null);

  ngOnInit() {
    this.usuario.set(this.session.mc?.user ?? null);
    this.contarRecibidas();

    // Entrar o salir cambia qué hacen Alertas, Perfil y Comparte.
    this.event.userEvent$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async () => {
        const usuario = await this.session.refrescar();
        this.usuario.set(usuario ?? null);
        this.contarRecibidas();
      });
  }

  /**
   * Las recibidas sin leer se piden sólo donde se ve la barra (móvil, < 993 px), en el
   * navegador y con una cuenta verificada: las sin verificar reciben 403 y el interceptor lo
   * convierte en el modal de «valida tu email», igual que en la campana.
   */
  private contarRecibidas() {
    const usuario = this.usuario();
    if (!this.esNavegador || !usuario || usuario.role === 'unverified') return;
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 992px)').matches) return;
    void this.alertas.contarRecibidasSinLeer();
  }

  /** Alertas o Perfil sin sesión: no se navega, se abre el acceso. */
  async pulsarProtegido(ev: Event) {
    if (this.usuario()) return true;

    ev.preventDefault();
    await this.quick.openLoginBox('login');
    return false;
  }

  /** El mismo desplegable de «Sube tu chollo» de la cabecera. */
  async compartir(ev: Event) {
    if (!this.usuario()) {
      await this.quick.openLoginBox('login');
      return;
    }

    const { ShareCholloPannelComponent } = await import('../share-chollo-pannel/share-chollo-pannel.component');
    const popover = await this.popover.create({
      component: ShareCholloPannelComponent,
      cssClass: 'ShareCholloPannelComponent no_bg ng_bs border_radius_0 panel_popover toolbar_popover barra_inferior_popover',
      event: ev,
      translucent: true,
      componentProps: { url: this.url() },
    });

    await popover.present();
  }
}
